import { Ionicons } from "@expo/vector-icons";
import { Icon, IconButton, Input, Text, useTheme, View } from "native-base";
import React from "react";
import type { TFunction } from "react-i18next";
import { useSelector } from "react-redux";

import { createIngredientDescriptionComponent, getServingDescriptionText } from "../helpers/foodHelpers";
import { formatNumberAsDecimal, formatNumberAsWholeNumber } from "../helpers/generalHelpers";
import styles from "../screens/RecipeDetailScreenStyle";
import type { GeneratedRecipeMealIngredient, Ingredient } from "../services/backendTypes";
import { userSelector } from "../slices/userSlice";

type GeneratedOrRealIngredient = GeneratedRecipeMealIngredient | Ingredient;

export const areIngredientsEqual = (a: GeneratedOrRealIngredient, b: GeneratedOrRealIngredient): boolean => {
  if ("modified" in a && "modified" in b) {
    // Ingredient, not GeneratedRecipeMealIngredient
    return a.id === b.id && a.modified === b.modified;
  }

  if (a.quantity !== b.quantity) {
    return false;
  }

  if (a.suggested_serving !== b.suggested_serving) {
    return false;
  }

  return true;
};

type IngredientComponentProps = {
  ingredientIndex: number;
  ingredient: GeneratedRecipeMealIngredient | Ingredient;
  portions: number;
  editable: boolean;
  onEditIngredientQuantity?: (ingredient: Ingredient, quantity: number) => Promise<void>;
  t: TFunction;
  quantityInputState: string;
  setQuantityInputState: (value: string) => void;
  onPressIngredientSwapButton: () => void;
  isLoading: boolean;
};

export const IngredientComponent = ({
  ingredientIndex,
  ingredient,
  portions,
  editable,
  onEditIngredientQuantity,
  t,
  quantityInputState,
  setQuantityInputState,
  onPressIngredientSwapButton,
  isLoading,
}: IngredientComponentProps): JSX.Element => {
  const theme = useTheme();

  const user = useSelector(userSelector);

  if (!ingredient.suggested_serving) {
    throw new Error("Suggested serving is missing");
  }

  const getIndividualMacroForDisplay = (value: number): string =>
    formatNumberAsWholeNumber(value * ingredient.quantity);

  const kcalDescription = `${getIndividualMacroForDisplay(ingredient.suggested_serving.kcal)} ${t("general.kcal")}`;
  const proteinDescription = `${getIndividualMacroForDisplay(ingredient.suggested_serving.protein)}g ${t(
    "general.protein"
  )}`;
  const macrosDescription = `${kcalDescription}, ${proteinDescription}`;

  const { servingInGrams, nonEditableDescription, ingredientInGrams, isServingInGrams } = getServingDescriptionText(
    ingredient.suggested_serving,
    ingredient
  );

  const onBlurIngredientInput = async (): Promise<void> => {
    if (!quantityInputState) {
      // NOTE: An empty string means there has been no change
      return;
    }

    const quantity = parseFloat(quantityInputState.replace(",", "."));

    if (Number.isNaN(quantity)) {
      alert(t("recipe_detail.ingredient_quantity_input.not_a_number_error_text"));
      return;
    }

    if (quantity === 0) {
      alert(t("recipe_detail.ingredient_quantity_input.delete_with_delete_button_error_text"));
      return;
    }

    // TODO: we only edit RecipeMeals, not GeneratedRecipeMeals so
    // ingredient not of type GeneratedRecipeMealIngredient
    // It would be good if we could enforce a type check when this screen loads
    if (!onEditIngredientQuantity) throw new Error("onEditIngredientQuantity undefined");

    await onEditIngredientQuantity(ingredient as Ingredient, quantity);
  };

  const onPressDeleteIngredient = async (): Promise<void> => {
    if (!onEditIngredientQuantity) {
      throw new Error("onEditIngredientQuantity undefined");
    }
    await onEditIngredientQuantity(ingredient as Ingredient, 0);
  };

  return (
    <View
      key={`${ingredientIndex}-${ingredient.quantity}`}
      style={styles.ingredientRow}
      testID={`recipeDetailIngredient-component-${ingredientIndex}`}
      nativeID={`recipeDetailIngredient-component-${ingredientIndex}`}
    >
      <View style={{ width: "60%" }}>
        <Text color="gray.500" fontWeight="500" fontSize="md" testID="name">
          {ingredient.name}
        </Text>
        <Text color="gray.500" testID="macrosDescription">
          {macrosDescription}
        </Text>
      </View>

      <View style={{ width: "40%", alignItems: "flex-end" }}>
        {editable ? (
          <>
            <Input
              selectTextOnFocus
              keyboardType="numeric"
              returnKeyType="done"
              defaultValue={formatNumberAsDecimal(ingredient.quantity, 2)}
              testID={`ingredient-quantity-input-${ingredientIndex}`}
              nativeID={`ingredient-quantity-input-${ingredientIndex}`}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onBlur={onBlurIngredientInput}
              onChangeText={(text) => setQuantityInputState(text.replace(",", "."))}
              InputRightElement={
                <IconButton
                  isDisabled={isLoading}
                  icon={<Icon as={Ionicons} name="trash-outline" />}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onPress={onPressDeleteIngredient}
                  testID={`ingredient-${ingredientIndex}-delete-button`}
                />
              }
              InputLeftElement={
                <IconButton
                  isDisabled={isLoading}
                  ml="1"
                  size={"sm"}
                  onPress={onPressIngredientSwapButton}
                  _icon={{
                    as: Ionicons,
                    name: "swap-vertical-outline",
                  }}
                  testID={`recipeDetailIngredient-swap-button-${ingredientIndex}`}
                />
              }
            />
            {createIngredientDescriptionComponent(ingredient, theme, user)}
          </>
        ) : (
          <View testID="nonEditableDescription">
            <Text color="gray.500">{nonEditableDescription}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

export const MemoizedIngredientComponent = React.memo(IngredientComponent, (prevProps, nextProps): boolean =>
  areIngredientsEqual(prevProps.ingredient, nextProps.ingredient)
);
